import { apiDelete, apiGet, apiPost, request } from '@/shared/utils/requestBuilder';
import type { SolicitacaoAssinatura } from '@/types/assinatura';

export const assinaturaService = {
  obterRevisao: async (processoId: string) => {
    const response = await apiGet(`/processos/${processoId}/revisao`);
    const data = await response.json();
    return data.data;
  },

  validarCertificado: async (processoId: string, pfx: File, senha: string) => {
    const formData = new FormData();
    formData.append('pfx', pfx);
    formData.append('senha', senha);

    const response = await request({
      endpoint: `/processos/${processoId}/validar-certificado`,
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    return data.data;
  },

  assinarProcesso: async (processoId: string, pfx: File, senha: string) => {
    const formData = new FormData();
    formData.append('pfx', pfx);
    formData.append('senha', senha);

    const response = await request({
      endpoint: `/processos/${processoId}/assinar`,
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    return data.data;
  },

  enviarParaAdvogado: async (processoId: string, email: string, mensagem?: string) => {
    const response = await apiPost(`/processos/${processoId}/enviar-para-advogado`, {
      email,
      mensagem,
    });
    const data = await response.json();
    return data.data;
  },

  listarAssinaturas: async (processoId: string) => {
    const response = await apiGet(`/processos/${processoId}/assinaturas`);
    const data = await response.json();
    return data.data;
  },

  assinarViaToken: async (token: string, pfx: File, senha: string) => {
    const formData = new FormData();
    formData.append('pfx', pfx);
    formData.append('senha', senha);

    const response = await request({
      endpoint: `/assinaturas/${token}/assinar`,
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    return data.data;
  },

  verificarStatusToken: async (token: string) => {
    const response = await apiGet(`/assinaturas/${token}/status`);
    const data = await response.json();
    return data.data;
  },

  cancelarSolicitacao: async (solicitacaoId: string) => {
    const response = await apiDelete(`/assinaturas/${solicitacaoId}/cancelar`);
    const data = await response.json();
    return data.data;
  },

  listarPorAdvogado: async (advogadoId: string): Promise<SolicitacaoAssinatura[]> => {
    const response = await apiGet(`/advogados/${advogadoId}/assinaturas`);
    const result = await response.json();

    const rawData = result.data?.data || [];

    if (!Array.isArray(rawData)) {
      console.warn('listarPorAdvogado: result.data.data não é um array', result);
      return [];
    }

    const raw = rawData as Array<
      Omit<SolicitacaoAssinatura, 'solicitadoEm' | 'prazoLimite' | 'respondidoEm'> & {
        solicitadoEm: string;
        prazoLimite?: string;
        respondidoEm?: string;
      }
    >;
    return raw.map((it) => ({
      ...it,
      solicitadoEm: new Date(it.solicitadoEm),
      prazoLimite: it.prazoLimite ? new Date(it.prazoLimite) : undefined,
      respondidoEm: it.respondidoEm ? new Date(it.respondidoEm) : undefined,
    }));
  },

  listarSolicitacoes: async (filtros?: {
    status?: string[];
    numeroProcesso?: string;
    solicitante?: string;
    dataInicio?: string;
    dataFim?: string;
  }): Promise<SolicitacaoAssinatura[]> => {
    const params = new URLSearchParams();
    if (filtros?.status) params.append('status', filtros.status.join(','));
    if (filtros?.numeroProcesso) params.append('numeroProcesso', filtros.numeroProcesso);
    if (filtros?.solicitante) params.append('solicitante', filtros.solicitante);
    if (filtros?.dataInicio) params.append('dataInicio', filtros.dataInicio);
    if (filtros?.dataFim) params.append('dataFim', filtros.dataFim);

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/solicitacoes-assinatura${query}`);
    const result = await response.json();

    const rawData = result.data?.data || [];

    if (!Array.isArray(rawData)) {
      console.warn('listarSolicitacoes: result.data.data não é um array', result);
      return [];
    }

    const raw = rawData as Array<
      Omit<SolicitacaoAssinatura, 'solicitadoEm' | 'prazoLimite' | 'respondidoEm'> & {
        solicitadoEm: string;
        prazoLimite?: string;
        respondidoEm?: string;
      }
    >;
    return raw.map((it) => ({
      ...it,
      solicitadoEm: new Date(it.solicitadoEm),
      prazoLimite: it.prazoLimite ? new Date(it.prazoLimite) : undefined,
      respondidoEm: it.respondidoEm ? new Date(it.respondidoEm) : undefined,
    }));
  },

  rejeitarSolicitacao: async (solicitacaoId: string, motivo: string) => {
    const response = await apiPost(`/solicitacoes-assinatura/${solicitacaoId}/rejeitar`, {
      motivo,
    });
    const data = await response.json();
    return data.data;
  },
};
